import { registerRoute } from '../router';
import { apiFetch, ApiError } from '../api';
import { escapeHtml } from '../format';
import { openModal } from '../components/modal';

interface Tenant {
  id: string;
  name: string;
  status: 'active' | 'disabled';
  createdAt: string;
}

let tenants: Tenant[] = [];

async function loadTenants(container: HTMLElement): Promise<void> {
  tenants = await apiFetch<Tenant[]>('/api/tenants');
  renderList(container);
}

function renderList(container: HTMLElement): void {
  const listEl = container.querySelector('#tenants-list')!;

  listEl.innerHTML = tenants
    .map(
      (t) => `
    <div class="card" style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">
      <div>
        <div style="font-weight:600;">${escapeHtml(t.name)}</div>
        <div style="color:var(--clr-text-muted);font-size:0.85rem;margin-top:4px;">${escapeHtml(t.id)}</div>
      </div>
      <div style="display:flex;align-items:center;gap:10px;">
        <span class="badge ${t.status === 'active' ? 'badge--success' : 'badge--muted'}">${t.status}</span>
        <button class="btn btn--ghost" data-action="edit" data-id="${t.id}">Edit</button>
      </div>
    </div>`,
    )
    .join('');

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="edit"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tenant = tenants.find((t) => t.id === btn.dataset.id);
      if (tenant) openEditModal(container, tenant);
    });
  });
}

function openCreateModal(container: HTMLElement): void {
  const { root, close } = openModal(
    'New tenant',
    `
    <form id="tenant-form">
      <div class="field">
        <label for="tenant-name">Name</label>
        <input class="input" id="tenant-name" required>
      </div>
      <p style="font-size:0.8rem;color:var(--clr-text-muted);margin-bottom:14px;">Add members and configure providers from within the tenant afterward — creating it here only reserves the name.</p>
      <p class="error-text" id="tenant-error"></p>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn--secondary" id="tenant-cancel">Cancel</button>
        <button type="submit" class="btn btn--primary">Create</button>
      </div>
    </form>
  `,
  );

  root.querySelector('#tenant-cancel')?.addEventListener('click', close);

  root.querySelector('#tenant-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const errorEl = root.querySelector('#tenant-error')!;
    errorEl.textContent = '';

    const name = (root.querySelector('#tenant-name') as HTMLInputElement).value.trim();

    try {
      await apiFetch('/api/tenants', { method: 'POST', body: JSON.stringify({ name }) });
      close();
      await loadTenants(container);
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? err.message : 'Something went wrong.';
    }
  });
}

function openEditModal(container: HTMLElement, tenant: Tenant): void {
  const { root, close } = openModal(
    'Edit tenant',
    `
    <form id="tenant-edit-form">
      <div class="field">
        <label for="edit-tenant-name">Name</label>
        <input class="input" id="edit-tenant-name" required value="${escapeHtml(tenant.name)}">
      </div>
      <div class="field">
        <label for="edit-tenant-status">Status</label>
        <select class="input" id="edit-tenant-status">
          <option value="active" ${tenant.status === 'active' ? 'selected' : ''}>active</option>
          <option value="disabled" ${tenant.status === 'disabled' ? 'selected' : ''}>disabled</option>
        </select>
      </div>
      <p class="error-text" id="tenant-edit-error"></p>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn--secondary" id="tenant-edit-cancel">Cancel</button>
        <button type="submit" class="btn btn--primary">Save changes</button>
      </div>
    </form>
  `,
  );

  root.querySelector('#tenant-edit-cancel')?.addEventListener('click', close);

  root.querySelector('#tenant-edit-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const errorEl = root.querySelector('#tenant-edit-error')!;
    errorEl.textContent = '';

    const payload = {
      name: (root.querySelector('#edit-tenant-name') as HTMLInputElement).value.trim(),
      status: (root.querySelector('#edit-tenant-status') as HTMLSelectElement).value as 'active' | 'disabled',
    };

    try {
      await apiFetch(`/api/tenants/${tenant.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
      close();
      await loadTenants(container);
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? err.message : 'Something went wrong.';
    }
  });
}

registerRoute({
  path: '/tenants',
  title: 'Tenants',
  render: ({ container }) => {
    container.innerHTML = `
      <div class="page-header" style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;">
        <div>
          <h1>Tenants</h1>
          <p>Cross-tenant — visible to super-admins only.</p>
        </div>
        <button class="btn btn--primary" id="new-tenant-btn">+ New tenant</button>
      </div>
      <div id="tenants-list">Loading…</div>
    `;

    container.querySelector('#new-tenant-btn')?.addEventListener('click', () => openCreateModal(container));

    loadTenants(container).catch(() => {
      container.querySelector('#tenants-list')!.textContent = 'Could not load tenants.';
    });
  },
});
